import { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  AlertDialog,
  Button,
  Description,
  FieldError,
  Input,
  Label,
  Modal,
  Spinner,
  TextField,
  toast,
} from '@heroui/react';
import { IconPlus } from '@tabler/icons-react';
import { apiClient } from '@hs/shared';
import { trpc } from '@/lib/trpc';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';

interface SkuVirtualItemModalProps {
  open: boolean;
  skuId?: string | null;
  skuName?: string;
  /** SKU 当前单价（分），空数据新建时写入 priceSnapshot */
  unitPriceFen?: number | null;
  onCancel: () => void;
}

type VirtualItemRow = {
  id: string;
  skuId?: string | null;
  itemId?: string;
  itemName?: string | null;
  priceSnapshot?: number | string | null;
  itemImgUrl?: string | null;
  itemRemark?: string | null;
  uploadStatus?: string | null;
  publishStatus?: string | null;
};

const virtualItemSchema = z.object({
  itemId: z
    .string()
    .min(1, '道具 ID 不能为空')
    .max(64, '道具 ID 最长 64 字符')
    .regex(/^[A-Za-z0-9_-]+$/, '仅允许字母、数字、下划线、连字符'),
  itemName: z.string().min(1, '道具名称不能为空').max(20, '道具名称最多 20 字'),
  itemRemark: z.string().max(1024).optional().nullable(),
  itemImgUrl: z.string().url('请上传道具图片').min(1, '请上传道具图片'),
});

type VirtualItemFormValues = z.infer<typeof virtualItemSchema>;

function fenToYuanLabel(fen: number | string | null | undefined): string {
  if (fen == null || fen === '') return '-';
  const n = typeof fen === 'string' ? Number(fen) : fen;
  if (!Number.isFinite(n)) return '-';
  return `${(n / 100).toFixed(2)}元`;
}

function toFenNumber(value: number | string | null | undefined): number | null {
  if (value == null || value === '') return null;
  const n = typeof value === 'string' ? Number(value) : value;
  return Number.isFinite(n) ? n : null;
}

export function SkuVirtualItemModal({
  open,
  skuId,
  skuName,
  unitPriceFen,
  onCancel,
}: SkuVirtualItemModalProps) {
  const utils = trpc.useUtils();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  const handleModalOpenChange = useCallback((next: boolean) => {
    if (!next) onCancelRef.current();
  }, []);

  const { data: items = [], isLoading } = trpc.bizProduct.skuVirtualItem.listAll.useQuery(
    { skuId: skuId ?? undefined },
    { enabled: open && Boolean(skuId) }
  );

  const existing = (items[0] as VirtualItemRow | undefined) ?? null;
  const hasItem = Boolean(existing?.id);
  const isUploaded = existing?.uploadStatus === 'UPLOADED';
  const isPublished = existing?.publishStatus === 'PUBLISHED';
  const showUploadPublishButtons = !(isUploaded || isPublished);

  const displayPriceFen = hasItem
    ? toFenNumber(existing?.priceSnapshot)
    : toFenNumber(unitPriceFen);

  const form = useForm<VirtualItemFormValues>({
    resolver: zodResolver(virtualItemSchema) as any,
    defaultValues: {
      itemId: '',
      itemName: '',
      itemRemark: '',
      itemImgUrl: '',
    },
    mode: 'onChange',
  });
  const { reset, control, watch } = form;
  const errors = form.formState.errors;
  const itemImgUrl = watch('itemImgUrl');

  useEffect(() => {
    if (!open) {
      reset({ itemId: '', itemName: '', itemRemark: '', itemImgUrl: '' });
      setDeleteOpen(false);
      return;
    }
    if (existing) {
      reset({
        itemId: existing.itemId || '',
        itemName: existing.itemName || '',
        itemRemark: existing.itemRemark || '',
        itemImgUrl: existing.itemImgUrl || '',
      });
    } else {
      reset({ itemId: '', itemName: '', itemRemark: '', itemImgUrl: '' });
    }
  }, [open, existing, reset]);

  const createMutation = trpc.bizProduct.skuVirtualItem.create.useMutation({
    onSuccess: () => {
      toast.success('虚拟道具已保存');
      void utils.bizProduct.skuVirtualItem.listAll.invalidate();
    },
    onError: (error) => {
      toast.danger('保存失败', { description: error.message });
    },
  });

  const deleteMutation = trpc.bizProduct.skuVirtualItem.delete.useMutation({
    onSuccess: () => {
      toast.success('虚拟道具已删除');
      setDeleteOpen(false);
      void utils.bizProduct.skuVirtualItem.listAll.invalidate();
    },
    onError: (error) => {
      toast.danger('删除失败', { description: error.message });
    },
  });

  const onSubmit = async (values: VirtualItemFormValues) => {
    if (!skuId) {
      toast.danger('缺少 SKU');
      return;
    }
    const price = toFenNumber(unitPriceFen);
    if (price == null || price < 1) {
      toast.danger('当前 SKU 无有效单价，无法创建虚拟道具');
      return;
    }
    await createMutation.mutateAsync({
      skuId,
      itemId: values.itemId,
      itemName: values.itemName,
      priceSnapshot: price,
      itemImgUrl: values.itemImgUrl,
      itemRemark: values.itemRemark || null,
      uploadStatus: 'NOT_UPLOADED',
      publishStatus: 'NOT_PUBLISHED',
    });
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || hasItem) return;
    if (!file.type.match(/^image\/(jpeg|jpg|png)$/i)) {
      toast.warning('请上传 JPG 或 PNG 图片');
      return;
    }
    setImageUploading(true);
    try {
      const res = await apiClient.uploadFile({
        file,
        onProgress: () => {},
      });
      if (res.url) {
        form.setValue('itemImgUrl', res.url, { shouldValidate: true });
      }
    } catch (error) {
      const message =
        error instanceof Error && error.message ? error.message : '图片上传失败';
      toast.danger(message);
    } finally {
      setImageUploading(false);
    }
  };

  const statusAddedLabel = hasItem ? '已添加' : '未添加';
  const statusUploadLabel = hasItem && isUploaded ? '已上传' : '未上传';
  const statusPublishLabel = hasItem && isPublished ? '已发布' : '未发布';

  const isSaving = createMutation.isPending;
  const formDisabled = hasItem || isSaving;

  return (
    <>
      <Modal isOpen={open} onOpenChange={handleModalOpenChange}>
        <RacDialogTriggerAnchor />
        <Modal.Backdrop>
          <Modal.Container placement="center" scroll="inside" size="lg">
            <Modal.Dialog className="max-w-xl">
              <Modal.CloseTrigger />
              <Modal.Header className="gap-1">
                <Modal.Heading className="text-xl font-medium">虚拟道具</Modal.Heading>
                <Description className="text-sm">SKU：{skuName || '-'}</Description>
              </Modal.Header>
              <Modal.Body className="flex flex-col gap-5">
                {isLoading ? (
                  <div className="flex justify-center py-10">
                    <Spinner />
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="flex flex-col gap-1">
                        <span className="text-sm font-medium text-foreground">是否添加</span>
                        <div className="border-t border-default-200 pt-2 text-sm text-muted">
                          {statusAddedLabel}
                        </div>
                      </div>
                      <div className="flex flex-col gap-1">
                        <span className="text-sm font-medium text-foreground">是否上传</span>
                        <div className="border-t border-default-200 pt-2 text-sm text-muted">
                          {statusUploadLabel}
                        </div>
                      </div>
                      <div className="flex flex-col gap-1">
                        <span className="text-sm font-medium text-foreground">是否发布</span>
                        <div className="border-t border-default-200 pt-2 text-sm text-muted">
                          {statusPublishLabel}
                        </div>
                      </div>
                    </div>

                    <form
                      id="sku-virtual-item-form"
                      className="flex flex-col gap-4"
                      onSubmit={form.handleSubmit(onSubmit as any)}
                    >
                      <Controller
                        control={control}
                        name="itemId"
                        render={({ field }) => (
                          <TextField
                            className="w-full"
                            isInvalid={!!errors.itemId}
                            isDisabled={formDisabled}
                          >
                            <Label className="text-sm font-medium">
                              道具ID <span className="text-danger">*</span>
                            </Label>
                            <Input
                              variant="secondary"
                              placeholder="请输入道具ID"
                              maxLength={64}
                              value={field.value}
                              onChange={(e) => field.onChange(e.target.value)}
                              onBlur={field.onBlur}
                              name={field.name}
                              ref={field.ref}
                            />
                            <Description className="text-xs">
                              仅允许字母、数字、下划线、连字符
                            </Description>
                            {errors.itemId ? (
                              <FieldError>{errors.itemId.message}</FieldError>
                            ) : null}
                          </TextField>
                        )}
                      />

                      <Controller
                        control={control}
                        name="itemName"
                        render={({ field }) => (
                          <TextField
                            className="w-full"
                            isInvalid={!!errors.itemName}
                            isDisabled={formDisabled}
                          >
                            <Label className="text-sm font-medium">
                              道具名称 <span className="text-danger">*</span>
                            </Label>
                            <Input
                              variant="secondary"
                              placeholder="请输入道具名称"
                              maxLength={20}
                              value={field.value}
                              onChange={(e) => field.onChange(e.target.value)}
                              onBlur={field.onBlur}
                              name={field.name}
                              ref={field.ref}
                            />
                            {errors.itemName ? (
                              <FieldError>{errors.itemName.message}</FieldError>
                            ) : null}
                          </TextField>
                        )}
                      />

                      <div className="flex flex-col gap-1.5">
                        <Label className="text-sm font-medium">道具价格</Label>
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 min-w-[160px] items-center rounded-lg bg-default-100 text-sm">
                            {fenToYuanLabel(displayPriceFen)}
                          </div>
                          <span className="text-sm text-muted">道具价格禁止修改</span>
                        </div>
                      </div>

                      <Controller
                        control={control}
                        name="itemRemark"
                        render={({ field }) => (
                          <TextField className="w-full" isDisabled={formDisabled}>
                            <Label className="text-sm font-medium">道具备注</Label>
                            <Input
                              variant="secondary"
                              placeholder="请输入道具备注"
                              maxLength={1024}
                              value={field.value ?? ''}
                              onChange={(e) => field.onChange(e.target.value)}
                              onBlur={field.onBlur}
                              name={field.name}
                              ref={field.ref}
                            />
                          </TextField>
                        )}
                      />

                      <div className="flex flex-col gap-1.5">
                        <Label className="text-sm font-medium">
                          道具图片 <span className="text-danger">*</span>
                        </Label>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/jpeg,image/jpg,image/png"
                          className="hidden"
                          disabled={formDisabled || imageUploading}
                          onChange={(e) => void handleImageChange(e)}
                        />
                        <button
                          type="button"
                          disabled={formDisabled || imageUploading}
                          onClick={() => fileInputRef.current?.click()}
                          className="relative flex size-24 items-center justify-center overflow-hidden rounded-lg border border-dashed border-default-300 bg-default-100 text-muted transition-colors hover:border-default-400 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {itemImgUrl ? (
                            <img
                              src={itemImgUrl}
                              alt="道具预览"
                              className="size-full object-cover"
                            />
                          ) : imageUploading ? (
                            <Spinner size="sm" />
                          ) : (
                            <IconPlus className="size-6" stroke={1.5} />
                          )}
                        </button>
                        {errors.itemImgUrl ? (
                          <FieldError>{errors.itemImgUrl.message}</FieldError>
                        ) : null}
                      </div>
                    </form>

                    <div className="flex flex-col gap-1">
                      <span className="text-sm font-medium text-foreground">说明</span>
                      <p className="text-sm text-muted">
                        道具信息已创建后禁止修改，如需修改，请自小程序后台修改。
                      </p>
                    </div>
                  </>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button type="button" variant="ghost" onPress={onCancel}>
                  取消
                </Button>
                {!hasItem ? (
                  <Button
                    variant="primary"
                    isDisabled={isSaving || isLoading || !skuId}
                    onPress={() => void form.handleSubmit(onSubmit as any)()}
                  >
                    {isSaving ? '保存中…' : '保存'}
                  </Button>
                ) : (
                  <Button variant="danger" onPress={() => setDeleteOpen(true)}>
                    删除
                  </Button>
                )}
                {showUploadPublishButtons ? (
                  <>
                    <Button
                      variant="secondary"
                      isDisabled={!hasItem}
                      onPress={() => toast.warning('上传功能稍后完善')}
                    >
                      上传
                    </Button>
                    <Button
                      variant="secondary"
                      isDisabled={!hasItem}
                      onPress={() => toast.warning('发布功能稍后完善')}
                    >
                      发布
                    </Button>
                  </>
                ) : null}
              </Modal.Footer>
            </Modal.Dialog>
          </Modal.Container>
        </Modal.Backdrop>
      </Modal>

      <AlertDialog isOpen={deleteOpen} onOpenChange={setDeleteOpen}>
        <RacDialogTriggerAnchor />
        <AlertDialog.Backdrop>
          <AlertDialog.Container placement="center">
            <AlertDialog.Dialog>
              <AlertDialog.Header>
                <AlertDialog.Heading>确认删除</AlertDialog.Heading>
              </AlertDialog.Header>
              <AlertDialog.Body>
                确定要删除道具「{existing?.itemName ?? existing?.itemId}」吗？
              </AlertDialog.Body>
              <AlertDialog.Footer>
                <Button variant="secondary" onPress={() => setDeleteOpen(false)}>
                  取消
                </Button>
                <Button
                  variant="danger"
                  isDisabled={deleteMutation.isPending || !existing?.id}
                  onPress={() => {
                    if (existing?.id) void deleteMutation.mutateAsync({ id: existing.id });
                  }}
                >
                  {deleteMutation.isPending ? '删除中…' : '确认删除'}
                </Button>
              </AlertDialog.Footer>
            </AlertDialog.Dialog>
          </AlertDialog.Container>
        </AlertDialog.Backdrop>
      </AlertDialog>
    </>
  );
}
